import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { FormattedNumberTextField } from '../../common/FormattedNumberTextField';
import { ChevronDown } from 'lucide-react';

import { LLMModelFormValues, LLMProvider, LLMTaskTypeOption } from '../../../types/admin';
import { LLMModelJsonField } from './types';
import { ModelTokenPricingSection } from './ModelTokenPricingSection';
import { splitResolutionPrices } from './modelPricing';
import { ModelResolutionPricingSection } from './ModelResolutionPricingSection';
import { ModelUnitPricingSection } from './ModelUnitPricingSection';

export interface ModelBasicTabProps {
  values: LLMModelFormValues;
  modelPublicId?: string;
  providers: LLMProvider[];
  taskTypes: LLMTaskTypeOption[];
  isLoadingTaskTypes: boolean;
  jsonErrors: Partial<Record<LLMModelJsonField, string>>;
  onValueChange: <K extends keyof LLMModelFormValues>(
    field: K,
    value: LLMModelFormValues[K]
  ) => void;
  onTaskTypesChange: (codes: string[]) => void;
  onJsonFieldChange: (field: LLMModelJsonField, value: string) => void;
}

export const ModelBasicTab: React.FC<ModelBasicTabProps> = ({
  values,
  modelPublicId,
  providers,
  taskTypes,
  isLoadingTaskTypes,
  jsonErrors,
  onValueChange,
  onTaskTypesChange,
  onJsonFieldChange,
}) => {
  const taskTypeByCode = React.useMemo(
    () => new Map(taskTypes.map((taskType) => [taskType.code, taskType])),
    [taskTypes]
  );

  const { resolutionRows, otherRows } = React.useMemo(
    () => splitResolutionPrices(values.unitPrices ?? []),
    [values.unitPrices]
  );

  return (
    <Box className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormControl fullWidth>
        <InputLabel>Provider</InputLabel>
        <Select
          label="Provider"
          value={values.providerPublicId}
          onChange={(e) => onValueChange('providerPublicId', e.target.value)}
        >
          {providers.map((provider) => (
            <MenuItem key={provider.publicId} value={provider.publicId}>
              {provider.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth>
        <InputLabel>Usage Scope</InputLabel>
        <Select
          label="Usage Scope"
          value={values.usageScope}
          onChange={(e) =>
            onValueChange('usageScope', e.target.value as LLMModelFormValues['usageScope'])
          }
        >
          <MenuItem value="both">Both</MenuItem>
          <MenuItem value="user">User</MenuItem>
          <MenuItem value="internal">Internal</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth required>
        <InputLabel>Task Type</InputLabel>
        <Select
          multiple
          label="Task Type"
          value={values.taskTypeCodes}
          disabled={isLoadingTaskTypes}
          onChange={(e) => {
            const next = e.target.value;
            onTaskTypesChange(typeof next === 'string' ? next.split(',') : next);
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((code, index) => (
                <Chip
                  key={code}
                  size="small"
                  color={index === 0 ? 'primary' : 'default'}
                  label={taskTypeByCode.get(code)?.name ?? code}
                />
              ))}
            </Box>
          )}
        >
          {taskTypes.map((taskType) => (
            <MenuItem key={taskType.code} value={taskType.code}>
              <Checkbox checked={values.taskTypeCodes.includes(taskType.code)} />
              <ListItemText primary={taskType.name} />
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>The first selected task type is the primary one.</FormHelperText>
      </FormControl>
      <TextField
        fullWidth
        label="Model Code"
        value={values.code}
        onChange={(e) => onValueChange('code', e.target.value)}
        helperText="Provider model id"
      />
      <TextField
        fullWidth
        label="Name"
        value={values.name}
        onChange={(e) => onValueChange('name', e.target.value)}
      />
      <FormattedNumberTextField
        fullWidth
        label="Context Window"
        value={values.contextWindow}
        allowEmpty
        onValueChange={(value) => onValueChange('contextWindow', value)}
        inputProps={{ min: 1 }}
      />
      <TextField
        fullWidth
        label="Description"
        value={values.description ?? ''}
        onChange={(e) => onValueChange('description', e.target.value)}
      />
      <Box className="md:col-span-2">
        <Typography
          variant="subtitle2"
          className="mb-1 font-semibold text-gray-700 dark:text-slate-300"
        >
          Default Model Parameters
        </Typography>
        <Typography variant="caption" className="mb-3 block text-gray-500 dark:text-slate-400">
          Default values for this model. A capability or internal task can override temperature, max
          output tokens and top P for its own use case (Capability Management / Internal AI Tasks).
          Empty = the provider default.
        </Typography>
        <Box className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Default Temperature"
            value={values.defaultTemperature ?? ''}
            onChange={(e) =>
              onValueChange(
                'defaultTemperature',
                e.target.value === '' ? null : parseFloat(e.target.value)
              )
            }
            helperText="0.0 - 2.0, empty = provider default"
            inputProps={{ min: 0, max: 2, step: 0.1 }}
          />
          <FormattedNumberTextField
            fullWidth
            size="small"
            label="Default Max Output Tokens"
            value={values.defaultMaxOutputTokens ?? undefined}
            allowEmpty
            onValueChange={(value) => onValueChange('defaultMaxOutputTokens', value ?? null)}
            helperText="Max tokens in the answer, e.g. 2048, 4096, 8192"
            inputProps={{ min: 1, step: 128 }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Default Top P"
            value={values.defaultTopP ?? ''}
            onChange={(e) =>
              onValueChange(
                'defaultTopP',
                e.target.value === '' ? null : parseFloat(e.target.value)
              )
            }
            helperText="0.0 - 1.0, empty = provider default"
            inputProps={{ min: 0, max: 1, step: 0.05 }}
          />
          <FormattedNumberTextField
            fullWidth
            size="small"
            label="First Chunk Timeout (ms)"
            value={values.firstChunkTimeoutMs ?? undefined}
            allowEmpty
            onValueChange={(value) => onValueChange('firstChunkTimeoutMs', value ?? null)}
            helperText="Streaming: max wait for the first chunk, empty = default"
            inputProps={{ min: 1, step: 1000 }}
          />
          <FormattedNumberTextField
            fullWidth
            size="small"
            label="Stream Stall Timeout (ms)"
            value={values.streamStallTimeoutMs ?? undefined}
            allowEmpty
            onValueChange={(value) => onValueChange('streamStallTimeoutMs', value ?? null)}
            helperText="Streaming: max silence between chunks, empty = default"
            inputProps={{ min: 1, step: 1000 }}
          />
        </Box>
      </Box>
      <Box className="md:col-span-2">
        <ModelTokenPricingSection values={values} onValueChange={onValueChange} />
      </Box>
      <Box className="md:col-span-2">
        <ModelUnitPricingSection
          value={otherRows}
          taskTypeCode={values.taskTypeCode}
          modelPublicId={modelPublicId}
          onChange={(next) => onValueChange('unitPrices', [...next, ...resolutionRows])}
        />
      </Box>
      <Box className="md:col-span-2">
        <ModelResolutionPricingSection
          value={resolutionRows}
          modelPublicId={modelPublicId}
          onChange={(next) => onValueChange('unitPrices', [...otherRows, ...next])}
        />
      </Box>
      <Accordion className="md:col-span-2" disableGutters variant="outlined">
        <AccordionSummary expandIcon={<ChevronDown size={18} />}>
          <Typography variant="subtitle2">Other config keys (advanced)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth
            label="Config JSON"
            value={values.config ?? ''}
            onChange={(e) => onJsonFieldChange('config', e.target.value)}
            error={Boolean(jsonErrors.config)}
            helperText={
              jsonErrors.config ||
              'Keys other than the inputs above (e.g. requestFormat, speaker, request body overrides). Saved together with the inputs above, never overwritten by them.'
            }
            multiline
            rows={4}
            inputProps={{ className: 'font-mono text-xs' }}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
